// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from "react"
import type { GlossaryTermId } from "@bitebalance/i18n"
import type { ProductStateResponse } from "@bitebalance/contracts"
import { useApiClient } from "@/api/useApiClient"
import { useI18n, formatEurFromCents } from "@/i18n"
import { InfoButton } from "@/components/ui/InfoButton"
import { checkoutStatusLabel, glossaryTerm, goalDeltaLabel, goalStatusLabel } from "@/lib/glossary"
import { fetchBudgetEconomics } from "@/lib/productState"

type BudgetEconomics = ProductStateResponse["budgetEconomics"]

/**
 * "YOUR BUDGET OUTLOOK" (Budget Model v2, ADR-052 §11.2) — rendered ONLY
 * from the backend-owned product state (GET /v1/product-state); when that
 * read fails nothing renders, and with no current plan the honest
 * "outlook appears once you have a plan" line shows instead of rows.
 *
 * Rows: forecast weekly average (estimate), weekly goal (or the "previous
 * budget" row for an unclassified pre-v2 amount — plain wording,
 * maximum-checkout ⓘ, never "current"), the EXACT signed difference
 * (omitted when there is no goal), next shop estimate, pantry carryover,
 * and "Actual weekly average — Not tracked yet" (owner D-4: never derived).
 * Every row NAME comes from the canonical glossary; every ⓘ opens it.
 */
export function BudgetOutlook({ refreshKey = 0 }: { refreshKey?: number }) {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const [state, setState] = useState<BudgetEconomics | null>(null)

  useEffect(() => {
    let cancelled = false
    void fetchBudgetEconomics(api).then(res => {
      if (!cancelled) setState(res)
    })
    return () => {
      cancelled = true
    }
  }, [api, refreshKey])

  if (!state) return null

  const name = (id: GlossaryTermId) => glossaryTerm(id, locale).name
  const money = (cents: number) => formatEurFromCents(cents, locale)
  const current = state.current
  const forecast = state.forecastWeeklyAverage
  const legacy = state.budgetModel === "legacy_hard_cap"
  const economicsComplete = current?.economics?.complete === true

  return (
    <section
      aria-labelledby="budget-outlook-heading"
      data-testid="budget-outlook"
      className="flex flex-col gap-3 rounded-bb-md border border-line bg-paper p-5"
    >
      <h2 id="budget-outlook-heading" className="text-lg font-semibold text-ink">
        {t("views.budget.outlookTitle")}
      </h2>
      <p className="text-xs text-ink-2">{t("views.budget.outlookBasis")}</p>

      {!current ? (
        <p className="text-sm text-ink-2">{t("views.budget.noPlanYet")}</p>
      ) : (
        <dl className="flex flex-col divide-y divide-line text-sm">
          <Row label={name("forecast-weekly-average")} termId="forecast-weekly-average">
            {forecast.available && forecast.cents !== null
              ? `${money(forecast.cents)} ${t("views.budget.perWeek")}`
              : forecast.unavailableReason === "unpriced_rows"
                ? checkoutStatusLabel("unverifiable", locale)
                : goalStatusLabel("unavailable", locale)}
          </Row>

          {legacy ? (
            <Row
              label={t("views.budget.previousBudgetLabel", {
                amount: state.legacyWeeklyLimitCents !== null ? money(state.legacyWeeklyLimitCents) : "—",
              })}
              termId="maximum-checkout"
              hint={
                state.legacyWeeklyLimitCents !== null
                  ? t("views.budget.previousBudgetHint", { amount: money(state.legacyWeeklyLimitCents) })
                  : undefined
              }
            />
          ) : (
            <Row label={t("views.budget.weeklyGoalRow")} termId="weekly-spending-goal">
              {state.weeklyGoalCents !== null
                ? `${money(state.weeklyGoalCents)} ${t("views.budget.perWeek")}`
                : t("views.budget.noGoalSet")}
            </Row>
          )}

          {!legacy && state.weeklyGoalCents !== null && current.goal.deltaCents !== null && (
            <Row label={t("views.budget.differenceRow")}>
              {goalDeltaLabel(current.goal.deltaCents, locale)}
            </Row>
          )}

          {/* Owner invariant 2026-09-04 (completeness, fail closed): the
              persisted checkout is a complete estimate ONLY when the
              revision's economics say `complete === true`; false or absent
              → "Priced so far €X", never a total; an unverifiable checkout
              keeps its status word. The failed attempt's candidate numbers
              (`latestFailedAttempt`) are never read here. */}
          <Row label={name("next-shop-estimate")} termId="next-shop-estimate">
            {current.checkout.status === "unverifiable"
              ? checkoutStatusLabel("unverifiable", locale)
              : economicsComplete
                ? money(current.checkout.costCents)
                : `${t("views.budget.pricedSoFar")} ${money(current.checkout.costCents)}`}
          </Row>

          {economicsComplete && current.economics ? (
            <Row label={name("pantry-carryover")} termId="pantry-carryover">
              {money(current.economics.pantryCarryoverValueCents)}
            </Row>
          ) : (
            // Documented choice: hide the split + explain (a carryover summed
            // over priced rows only would misstate the number).
            <p className="py-2.5 text-xs text-ink-2" data-testid="outlook-economics-partial">
              {t("views.budget.partialEconomicsNote")}
            </p>
          )}

          <Row label={name("actual-weekly-average")} termId="actual-weekly-average" hint={t("views.budget.notTrackedHint")}>
            {t("views.budget.notTrackedYet")}
          </Row>
        </dl>
      )}
    </section>
  )
}

function Row({
  label,
  termId,
  hint,
  children,
}: {
  label: string
  termId?: GlossaryTermId
  hint?: string
  children?: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-0.5 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <dt className="flex items-center gap-1 text-ink-2">
          <span>{label}</span>
          {termId && <InfoButton termId={termId} />}
        </dt>
        {children !== undefined && <dd className="font-semibold text-ink">{children}</dd>}
      </div>
      {hint && <p className="text-xs text-ink-2">{hint}</p>}
    </div>
  )
}
